import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { ShippingAddress } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { CheckoutEmailStep } from "./checkout/CheckoutEmailStep";
import { CheckoutShippingStep } from "./checkout/CheckoutShippingStep";
import { CheckoutPaymentStep } from "./checkout/CheckoutPaymentStep";
import { DeliveryTypeEnum } from "@/types/DeliveryType";
import { getContrastTextColor } from "@/utils/colorUtils";

interface Product {
  _id: string;
  name?: string;
  price: number;
  payWhatYouCan?: boolean;
  deliveryType?: string;
  quantityRules?: { minimum: number; maximum: number };
}

interface CheckoutFlowProps {
  checkoutId: string;
  products: Product[];
  stripe: {
    publishableKey: string;
    accountId?: string;
  } | null;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
    };
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
  } | null;
  artistName?: string;
}

const STEP_MAP = { email: 1, shipping: 2, payment: 3 } as const;

export function CheckoutFlow({ checkoutId, products, stripe, checkoutSettings, artistName }: CheckoutFlowProps) {
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const step = stepParam ? (STEP_MAP[stepParam as keyof typeof STEP_MAP] ?? 1) : 1;
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [customerEmail, setCustomerEmail] = useState("");
  const [amountToPay, setAmountToPay] = useState("");
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripePromise = useMemo(() => {
    if (!stripe?.publishableKey) return null;
    const options: { stripeAccount?: string } = {};
    if (stripe.accountId) options.stripeAccount = stripe.accountId;
    return loadStripe(stripe.publishableKey, options);
  }, [stripe]);

  useEffect(() => {
    if (products.length > 0) {
      const initialQuantities: Record<string, number> = {};
      products.forEach((product) => {
        const min = product.quantityRules?.minimum ?? 1;
        initialQuantities[product._id] = min;
      });
      setQuantities(initialQuantities);
    }
  }, [products]);

  const handleQuantityChange = (productId: string, quantity: number) => {
    const product = products.find((p) => p._id === productId);
    const min = product?.quantityRules?.minimum ?? 1;
    const max = product?.quantityRules?.maximum ?? 10;
    const clamped = Math.max(min, Math.min(max, quantity));
    setQuantities((prev) => ({ ...prev, [productId]: clamped }));
  };

  const handleNext = () => {
    if (step === 1) {
      navigate(
        skipsShipping
          ? `/checkout/${checkoutId}/payment`
          : `/checkout/${checkoutId}/shipping`
      );
    } else if (step === 2) {
      navigate(`/checkout/${checkoutId}/payment`);
    }
  };

  const getEffectiveTotal = () => {
    const hasPayWhatYouCan = products.some((p) => p.payWhatYouCan);
    if (hasPayWhatYouCan && amountToPay) {
      const parsed = parseFloat(amountToPay);
      return !isNaN(parsed) && parsed >= getTotal() ? parsed : getTotal();
    }
    return getTotal();
  };

  const handlePaymentMethod = async (paymentMethodId: string, tipAmount?: number) => {
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodId,
          products: getCartItems().map((item) => ({
            productId: item.product._id,
            quantity: item.quantity,
          })),
          email: customerEmail,
          shipping: shippingAddress,
          total: getEffectiveTotal(),
          tipAmount: tipAmount,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Payment failed");
      }

      navigate(`/checkout/${checkoutId}/success`, {
        state: {
          cartItems: getCartItems(),
          total: getEffectiveTotal(),
          tipAmount: tipAmount ?? 0,
        },
      });
    } catch (err) {
      throw err;
    }
  };

  const canProceedToNextStep = () => {
    if (step === 1) {
      const hasPayWhatYouCan = products.some((p) => p.payWhatYouCan);
      const emailOk = customerEmail.trim() !== "";
      if (!hasPayWhatYouCan) return emailOk;
      const parsed = parseFloat(amountToPay);
      const amountOk = !isNaN(parsed) && parsed >= getTotal();
      return emailOk && amountOk;
    }
    if (step === 2 && !skipsShipping) {
      return (
        products.length > 0 &&
        Object.keys(quantities).length > 0 &&
        isAddressComplete &&
        shippingAddress !== null
      );
    }
    return false;
  };

  const getCartItems = () => {
    return products
      .map((product) => ({
        product,
        quantity: quantities[product._id] || 0,
      }))
      .filter((item) => item.quantity > 0);
  };

  const skipsShipping =
    getCartItems().length > 0 &&
    getCartItems().every(
      (item) => item.product.deliveryType === DeliveryTypeEnum.DIGITAL
    );

  useEffect(() => {
    if (stepParam === "shipping" && skipsShipping) {
      navigate(`/checkout/${checkoutId}/payment`, { replace: true });
    }
  }, [stepParam, skipsShipping, checkoutId, navigate]);

  const getTotal = () => {
    return getCartItems().reduce(
      (sum, item) => sum + (item.product.price || 0) * item.quantity,
      0
    );
  };

  const getCustomerName = () => shippingAddress?.name ?? "";

  const totalSteps = skipsShipping ? 2 : 3;
  const currentStepDisplay = skipsShipping
    ? stepParam === "email"
      ? 1
      : stepParam === "payment"
        ? 2
        : 1
    : step;

  const getStepNavPath = (s: number) => {
    if (skipsShipping) {
      if (s === 1) return `/checkout/${checkoutId}/email`;
      if (s === 2) return `/checkout/${checkoutId}/payment`;
    } else {
      if (s === 1) return `/checkout/${checkoutId}/email`;
      if (s === 2) return `/checkout/${checkoutId}/shipping`;
      if (s === 3) return `/checkout/${checkoutId}/payment`;
    }
    return `/checkout/${checkoutId}/email`;
  };

  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const buttonColor = checkoutSettings?.colors?.buttonColor || "#000000";
  const accentColor = checkoutSettings?.colors?.accentColor || "#000000";
  const textColor = getContrastTextColor(backgroundColor);
  const accentTextColor = getContrastTextColor(accentColor);

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor }}>
      <div
        className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
        style={{ color: textColor }}
      >
      <h1 className="text-2xl font-bold text-center">
        {artistName || ""}
      </h1>
      <div className="flex items-center justify-center gap-0 py-4">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s, i) => (
          <div key={s} className="flex items-center">
            {i > 0 && (
              <div
                className="h-px w-8 shrink-0 mx-1"
                style={{ backgroundColor: accentColor, opacity: 0.4 }}
              />
            )}
            <button
              type="button"
              onClick={() => {
                if (!isProcessing && s < currentStepDisplay) {
                  navigate(getStepNavPath(s));
                }
              }}
              disabled={isProcessing || s > currentStepDisplay}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
              style={
                s === currentStepDisplay
                  ? { backgroundColor: accentColor, color: accentTextColor }
                  : s < currentStepDisplay
                    ? {
                        border: "1px solid",
                        borderColor: accentColor,
                        backgroundColor: "transparent",
                        color: textColor,
                      }
                    : {
                        border: "1px solid rgba(128,128,128,0.3)",
                        backgroundColor: "rgba(128,128,128,0.2)",
                        color: "rgba(128,128,128,0.6)",
                      }
              }
            >
              {s}
            </button>
          </div>
        ))}
      </div>

      {step === 1 && (
        <CheckoutEmailStep
          email={customerEmail}
          onEmailChange={setCustomerEmail}
          products={products}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          total={getTotal()}
          amountToPay={amountToPay}
          onAmountToPayChange={setAmountToPay}
        />
      )}

      {step === 2 && !skipsShipping && (
        <CheckoutShippingStep
          products={products}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          shippingAddress={shippingAddress}
          onShippingAddressChange={setShippingAddress}
          onAddressCompleteChange={setIsAddressComplete}
          stripePromise={stripePromise}
          total={getEffectiveTotal()}
          checkoutSettings={checkoutSettings}
        />
      )}

      {step === 3 && stripePromise && (
        <Elements stripe={stripePromise}>
          <CheckoutPaymentStep
            products={products}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            cartItems={getCartItems()}
            total={getEffectiveTotal()}
            name={getCustomerName()}
            email={customerEmail}
            onPaymentMethod={handlePaymentMethod}
            isProcessing={isProcessing}
            error={error}
            setError={setError}
            setIsProcessing={setIsProcessing}
            checkoutSettings={checkoutSettings}
          />
        </Elements>
      )}

      {stepParam !== "payment" && (
        <div
          className="flex justify-end gap-2 pt-4 border-t mt-auto"
          style={{ borderColor: textColor === "black" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)" }}
        >
          <Button
            type="button"
            onClick={handleNext}
            disabled={!canProceedToNextStep()}
            style={{
              backgroundColor: buttonColor,
              color: getContrastTextColor(buttonColor),
            }}
            className="hover:opacity-90"
          >
            Continue
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}
